'use client';

import { getCountryIso, isGermany, isDK, type CountryIso } from 'utils';
import { useEffect, useRef } from 'react';
import { useCartAddressFormStore, type CartAddressFormType } from 'stores';
import type { CartAddressFieldsProps, PickupPointSelection } from '../../types';
import { PickupPointSelector } from '../pickup-points/PickupPointSelector';
import { ADDRESS_COUNTRY_CODE_ALIASES, DESTINATION_TYPES, LOCALES_REQUIRING_COUNTRY_MATCH } from '../../constants';
import styles from '../../CartAddressStep.module.css';
import { createAddressFormData } from './utils';
import { AddressIdentityFields } from './partials/AddressIdentityFields';
import { AddressDetailsFields } from './AddressDetailsFields';
import { PickupPointFields } from './partials/PickupPointFields';
import { AddressLocationFields } from './partials/AddressLocationFields';

function normalizeCountryForForm(country: string): string {
  const normalizedCountry = country.toUpperCase();

  return (
    ADDRESS_COUNTRY_CODE_ALIASES[normalizedCountry as keyof typeof ADDRESS_COUNTRY_CODE_ALIASES] ?? normalizedCountry
  );
}

function getFormType(company: boolean, pickupPoint: boolean): CartAddressFormType {
  if (pickupPoint) {
    return DESTINATION_TYPES.PICKUP_POINT;
  }

  if (company) {
    return DESTINATION_TYPES.COMPANY;
  }

  return DESTINATION_TYPES.PRIVATE;
}

/**
 * Renders the reusable private, company, or pickup-point address fields.
 * @param props - Address data, form mode, locale, localized constants, and optional change handler.
 * @returns The address input fields for the selected destination type.
 */
export function CartAddressFields({
  address,
  locale,
  company,
  constants,
  fieldNamePrefix = '',
  formId,
  pickupPoint = false,
  initialPickupPoints,
  onChange,
  onFieldChange,
  fieldErrors = {},
  formScope = 'shipping',
  isInDialog = false
}: CartAddressFieldsProps) {
  const currentCountry = getCountryIso(locale).toUpperCase() as CountryIso;
  const initialCountry = address?.country ? normalizeCountryForForm(address.country) : currentCountry;
  const showCountrySelection = !LOCALES_REQUIRING_COUNTRY_MATCH.includes(locale);
  const formType = getFormType(company, pickupPoint);
  const showCountryField = !pickupPoint || isDK(locale) || isGermany(locale);
  const isCountryReadOnly = !showCountrySelection || (pickupPoint && isGermany(locale));
  const storedFormData = useCartAddressFormStore(state => state.formData[formScope][formType]);
  const setFormData = useCartAddressFormStore(state => state.setFormData);
  const formData = storedFormData ?? createAddressFormData({ address, company, pickupPoint, initialCountry });
  const submittedCountry =
    pickupPoint && isGermany(locale) ? currentCountry : normalizeCountryForForm(formData.country || currentCountry);
  const fieldName = (name: string) => `${fieldNamePrefix}${name}`;
  const fieldError = (name: string) => fieldErrors[name];

  const handlePickupPointChange = (value: PickupPointSelection) => {
    onFieldChange?.('stationNumber');
    onFieldChange?.('zip');
    onFieldChange?.('postcode');
    onFieldChange?.('city');
    const formDataWithPickupPoint = { ...formData, ...value };
    setFormData(formScope, formType, formDataWithPickupPoint);
  };

  const handleCountryChange = (country: string) => {
    const formDataWithCountry = {
      ...formData,
      country
    };
    onFieldChange?.('country');
    onChange?.(formDataWithCountry);
    setFormData(formScope, formType, formDataWithCountry);
  };
  const previousFormType = useRef(formType);
  const previousAddress = useRef(address);

  // Keep the store in sync with the active form mode and streamed address data without overwriting user input.
  useEffect(() => {
    if (previousFormType.current !== formType) {
      previousFormType.current = formType;

      if (!storedFormData) {
        setFormData(
          formScope,
          formType,
          createAddressFormData({ address: undefined, company, pickupPoint, initialCountry })
        );
      }

      return;
    }

    if (!storedFormData) {
      setFormData(formScope, formType, formData);

      return;
    }

    if (previousAddress.current === address) {
      return;
    }

    // A changed address reference indicates refreshed server data, so rebuild the form values for that address.
    const refreshedFormData = createAddressFormData({ address, company, pickupPoint, initialCountry });
    setFormData(formScope, formType, refreshedFormData);
    previousAddress.current = address;
  }, [address, company, formScope, formType, formData, initialCountry, pickupPoint, setFormData, storedFormData]);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    const field = fieldNamePrefix && name.startsWith(fieldNamePrefix) ? name.slice(fieldNamePrefix.length) : name;
    onFieldChange?.(field);

    if (pickupPoint && isDK(locale) && (field === 'city' || field === 'zip')) {
      onFieldChange?.('stationNumber');
    }

    const updatedFormData = { ...formData, [field]: value };

    // Danish pickup-point results depend on city and zip; clear the selected station when either changes.
    if (pickupPoint && isDK(locale) && (field === 'city' || field === 'zip')) {
      updatedFormData.streetAddOn = '';
      updatedFormData.stationNumber = '';
    }

    setFormData(formScope, formType, updatedFormData);
  };

  const pickupPointSelector =
    pickupPoint && isDK(locale) ? (
      <PickupPointSelector
        city={formData.city}
        constants={constants}
        bottomSheetBackdropClassName={isInDialog ? styles.cartAddressDialogBackdrop : undefined}
        error={fieldError('stationNumber')}
        initialPickupPoints={initialPickupPoints}
        onChange={handlePickupPointChange}
        selectedStation={formData.streetAddOn}
        zip={formData.zip}
      />
    ) : undefined;

  return (
    <div className={styles.cartAddressFields}>
      <AddressIdentityFields
        formData={formData}
        company={company}
        constants={constants}
        fieldError={fieldError}
        fieldName={fieldName}
        formId={formId}
        onInputChange={handleInputChange}
        pickupPoint={pickupPoint}
      />
      {pickupPoint && (
        <PickupPointFields
          formData={formData}
          constants={constants}
          fieldError={fieldError}
          fieldName={fieldName}
          formId={formId}
          locale={locale}
          onInputChange={handleInputChange}
        />
      )}
      <AddressDetailsFields
        formData={formData}
        company={company}
        constants={constants}
        fieldError={fieldError}
        fieldName={fieldName}
        formId={formId}
        onInputChange={handleInputChange}
        pickupPoint={pickupPoint}
      />
      <AddressLocationFields
        formData={formData}
        constants={constants}
        currentCountry={currentCountry}
        fieldError={fieldError}
        fieldName={fieldName}
        formId={formId}
        isCountryReadOnly={isCountryReadOnly}
        isInDialog={isInDialog}
        locale={locale}
        onCountryChange={handleCountryChange}
        onInputChange={handleInputChange}
        pickupPointSelector={pickupPointSelector}
        showCountryField={showCountryField}
        showPhoneField={!pickupPoint || isDK(locale)}
        submittedCountry={submittedCountry}
      />
    </div>
  );
}
